import { Link } from '@tanstack/react-router'
import { Layers, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useCreatePool, usePools, useUnlinkChannel } from '@/api/endpoints/pools'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { LinkPoolDialog } from '@/features/pools/components/LinkPoolDialog'
import { languageLabel } from '@/features/scheduler/poolCards'
import { errorMessage } from '@/lib/errors'
import styles from './ChannelEditDialog.module.scss'

type Mode = 'own' | 'pool'
type Language = 'pt-br' | 'en'

interface SourceTabProps {
  slug: string
  name: string
  /** Pool que o canal usa hoje; ``null`` quando gera sozinho. */
  poolSlug: string | null
  isYoutube: boolean
}

/**
 * "Origem dos vídeos": gerar para o canal ou usar um pool. Age na hora, fora
 * do "Salvar" do diálogo: ligar e desligar movem vídeos e trocam a geração,
 * e cada um tem a própria confirmação. Ver docs/plan-video-pools.md §6.
 */
export function SourceTab({ slug, name, poolSlug, isYoutube }: SourceTabProps) {
  const { data: pools = [] } = usePools()
  const [mode, setMode] = useState<Mode>(poolSlug ? 'pool' : 'own')
  const [chosen, setChosen] = useState<string>(poolSlug ?? '')
  const [linkOpen, setLinkOpen] = useState(false)
  const [unlinkOpen, setUnlinkOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState(`${name} — pool`)
  const [newLanguage, setNewLanguage] = useState<Language>('pt-br')
  const createPool = useCreatePool()
  const unlink = useUnlinkChannel()

  const current = pools.find((p) => p.slug === poolSlug) ?? null
  const target = pools.find((p) => p.slug === chosen) ?? null

  if (!isYoutube) {
    return (
      <div className={styles.section}>
        <span className={styles.empty}>Por enquanto só canais do YouTube podem usar um pool.</span>
      </div>
    )
  }

  if (poolSlug) {
    return (
      <div className={styles.section}>
        <Callout variant="neutral" title={`Este canal publica do pool ${current?.name ?? poolSlug}`}>
          Ele não gera: a cada horário de publicação, pega o próximo vídeo livre do pool, e esse vídeo passa a ser só
          dele. Horários, formatos desativados e destinos continuam nas abas Upload e Destinos.
        </Callout>
        <div className={styles.sourceActions}>
          <Link className={styles.sourceLink} to="/pools/$slug" params={{ slug: poolSlug }}>
            <Layers aria-hidden />
            Abrir o pool
          </Link>
          <Button
            variant="secondary"
            onClick={() => {
              setUnlinkOpen(true)
            }}
          >
            Voltar a gerar para este canal
          </Button>
        </div>
        <DialogRoot open={unlinkOpen} onOpenChange={setUnlinkOpen}>
          <DialogContent
            role="alertdialog"
            title="Voltar a gerar para este canal?"
            description={`${name} sai do pool e volta a gerar sozinho, com uma cópia da configuração de geração do pool e a marca que tinha antes de entrar. Os vídeos que ele já pegou continuam com ele.`}
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setUnlinkOpen(false)
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  disabled={unlink.isPending}
                  leadingIcon={unlink.isPending ? <Spinner /> : undefined}
                  onClick={() => {
                    unlink.mutate(
                      { slug: poolSlug, channel: slug },
                      {
                        onSuccess: () => {
                          toast.success(`${name} voltou a gerar sozinho`)
                          setUnlinkOpen(false)
                          setMode('own')
                        },
                        onError: (err) => {
                          toast.error(errorMessage(err, 'Não foi possível tirar o canal do pool'))
                        },
                      },
                    )
                  }}
                >
                  Sair do pool
                </Button>
              </>
            }
          >
            <span className={styles.empty}>Os outros canais do pool continuam publicando dele.</span>
          </DialogContent>
        </DialogRoot>
      </div>
    )
  }

  return (
    <div className={styles.section}>
      <Segmented<Mode>
        aria-label="Origem dos vídeos"
        value={mode}
        onValueChange={setMode}
        options={[
          { value: 'own', label: 'Gerar para este canal' },
          { value: 'pool', label: 'Usar um pool' },
        ]}
      />
      {mode === 'own' ? (
        <span className={styles.empty}>
          O canal tem factory, agenda e marca próprias. É o modo de hoje; a configuração fica na aba Geração.
        </span>
      ) : (
        <>
          <span className={styles.empty}>
            O canal para de gerar e publica os vídeos livres do pool. Cada vídeo que ele pegar passa a ser só dele.
          </span>
          {pools.length > 0 ? (
            <div className={styles.sourceField}>
              <label htmlFor="source-pool">Pool</label>
              <SelectRoot {...(chosen ? { value: chosen } : {})} onValueChange={setChosen}>
                <SelectTrigger id="source-pool" aria-label="Pool">
                  <SelectValue placeholder="Escolha um pool" />
                </SelectTrigger>
                <SelectContent>
                  {pools.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      {`${p.name} · ${languageLabel(p.language)} · ${String(p.available)} disponíveis`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
          ) : null}
          <div className={styles.sourceActions}>
            <Button
              disabled={!target}
              onClick={() => {
                setLinkOpen(true)
              }}
            >
              Usar este pool
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<Plus />}
              onClick={() => {
                setCreating(true)
              }}
            >
              Criar pool a partir deste canal
            </Button>
          </div>
          {creating ? (
            <div className={styles.sourceCreate}>
              <div className={styles.sourceField}>
                <label htmlFor="source-new-pool">Nome do pool</label>
                <Input
                  id="source-new-pool"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value)
                  }}
                />
              </div>
              <div className={styles.sourceField}>
                <span>Idioma</span>
                <Segmented<Language>
                  aria-label="Idioma do pool"
                  size="sm"
                  value={newLanguage}
                  onValueChange={setNewLanguage}
                  options={[
                    { value: 'pt-br', label: 'PT-BR' },
                    { value: 'en', label: 'EN' },
                  ]}
                />
              </div>
              <span className={styles.empty}>
                O pool nasce com uma cópia da geração deste canal (factory, agenda, voz), sem a marca.
              </span>
              <Button
                disabled={!newName.trim() || createPool.isPending}
                leadingIcon={createPool.isPending ? <Spinner /> : undefined}
                onClick={() => {
                  createPool.mutate(
                    { name: newName.trim(), language: newLanguage, from_channel: slug },
                    {
                      onSuccess: (pool) => {
                        setChosen(pool.slug)
                        setCreating(false)
                        setLinkOpen(true)
                      },
                      onError: (err) => {
                        toast.error(errorMessage(err, 'Não foi possível criar o pool'))
                      },
                    },
                  )
                }}
              >
                Criar e continuar
              </Button>
            </div>
          ) : null}
        </>
      )}
      {target ? (
        <LinkPoolDialog
          open={linkOpen}
          onClose={() => {
            setLinkOpen(false)
          }}
          poolSlug={target.slug}
          poolName={target.name}
          channelSlug={slug}
          channelName={name}
        />
      ) : null}
    </div>
  )
}
